import { isEqual } from "lodash";

import { useCallback, useEffect, useRef, useState } from "react";

import { logger } from "interfaces/logger";

import {
  TalentHubExperienceLevel,
  TalentHubSearchFilters,
  TalentHubSortBy,
} from "utils/talentHubSearch";

import { fetchAuthWrapper } from "./backend";
import { supabase } from "./supabaseClient";
import { UserInfo } from "./user";

export type EducationLevel =
  | "self_taught"
  | "bachelors"
  | "masters"
  | "doctoral"
  | "professional";

export type DbTalentHubCandidate = {
  interview_call_id: string;
  interview_flow_id: string;
  ended_at: string;
  overall_score?: number;
  tagline_summary?: string;
  years_of_experience?: number;
  education_level?: EducationLevel;
  tools_and_skills?: string[];
  candidate_location?: string;
  candidate_id: string;
  candidate_first_name: string;
  candidate_last_name: string;
  candidate_full_name: string;
  candidate_photo_url?: string;
  candidate_email: string;
  role_title: string;
  is_published: boolean;
  is_publishable_since: string;
  team_id: string;
};

export type TalentHubCandidate = {
  interviewCallId: string;
  interviewFlowId: string;
  endedAt: string;
  overallScore?: number | null; // when these come from genie they can be null
  taglineSummary?: string | null; // when these come from genie they can be null
  yearsOfExperience?: number | null; // when these come from genie they can be null
  educationLevel?: EducationLevel | null; // when these come from genie they can be null
  toolsAndSkills?: string[] | null; // when these come from genie they can be null
  location?: string | null; // when these come from genie they can be null
  candidate: Pick<
    UserInfo,
    "id" | "firstName" | "fullName" | "lastName" | "avatarUrl" | "email"
  >;
  roleTitle: string;
  isPublished: boolean | null; // when these come from genie they can be null
  isPublishableSince: string;
};

export const formatTalenHubCandidate = (
  data: DbTalentHubCandidate
): TalentHubCandidate => ({
  interviewCallId: data.interview_call_id,
  interviewFlowId: data.interview_flow_id,
  endedAt: data.ended_at,
  overallScore: data.overall_score,
  taglineSummary: data.tagline_summary,
  yearsOfExperience: data.years_of_experience,
  educationLevel: data.education_level,
  toolsAndSkills: data.tools_and_skills,
  location: data.candidate_location,
  candidate: {
    id: data.candidate_id,
    firstName: data.candidate_first_name,
    fullName: data.candidate_full_name,
    lastName: data.candidate_last_name,
    avatarUrl: data.candidate_photo_url,
    email: data.candidate_email,
  },
  roleTitle: data.role_title,
  isPublished: data.is_published,
  isPublishableSince: data.is_publishable_since,
});

export const getTalentList = async ({
  pageNumber,
  pageSize,
  searchString,
  sortBy,
  scores,
  experienceLevels,
  isPublished,
}: {
  pageNumber: number;
  pageSize: number;
  searchString?: string;
  sortBy?: TalentHubSortBy;
  scores?: number[];
  experienceLevels?: TalentHubExperienceLevel[];
  isPublished?: boolean;
}): Promise<TalentHubCandidate[]> => {
  const { data, error } = await supabase.rpc("get_talent_hub_list", {
    page_number: pageNumber,
    page_size: pageSize,
    search_string: searchString,
    sort_by: sortBy,
    scores: scores,
    experience_levels: experienceLevels,
    p_is_published: isPublished,
  });

  if (error) {
    throw new Error(error.message);
  }

  return data.map((d: DbTalentHubCandidate) => formatTalenHubCandidate(d));
};

export const useTalentList = (
  talentSearch: TalentHubSearchFilters
): {
  isFinalPage: boolean;
  list: TalentHubCandidate[];
  loadMore: () => void;
  isLoadingMore: boolean;
} => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [list, setList] = useState<TalentHubCandidate[]>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const pageSize = 25;

  const loadPage = useCallback(
    async (page: number) => {
      if (isLoadingMore) return;
      setIsLoadingMore(true);

      const result = await getTalentList({
        pageNumber: page,
        pageSize: pageSize,
        searchString: talentSearch.searchString,
        sortBy: talentSearch.sortBy,
        scores: talentSearch.scores,
        experienceLevels: talentSearch.experienceLevels,
        isPublished: talentSearch.isPublished,
      });

      setList((state) => [...state, ...result]);

      setPageNumber(page);
      setIsLoadingMore(false);
    },
    [talentSearch, isLoadingMore]
  );

  // Fetch the first page if talentSearch changes
  const prevSearch = useRef(talentSearch);
  useEffect(() => {
    if (!isEqual(talentSearch, prevSearch.current)) {
      setList([]);
      setPageNumber(0);
      loadPage(1);
      prevSearch.current = talentSearch;
    }
  }, [talentSearch, loadPage]);

  return {
    isFinalPage: !!(list.length % pageSize),
    list,
    loadMore: () => loadPage(pageNumber + 1),
    isLoadingMore,
  };
};

export const requestAccessToTalentHubCandidateInterview = async ({
  interviewCallId,
}: {
  interviewCallId: string;
}): Promise<{ error?: string }> => {
  const res = await fetchAuthWrapper.post(
    "/be-api/request-access-to-talent-hub-candidate-interview",
    { interviewCallId }
  );

  if (res.status !== 200) {
    logger("Error requesting access to the candidate interview", "error");
    return { error: "Something unexpected happened" };
  }

  return {};
};

export const isExistingRequestFromRecruiterInTeam = async ({
  recruiterId,
  interviewFlowId,
  candidateId,
  teamId,
}: {
  recruiterId: string;
  interviewFlowId: string;
  candidateId: string;
  teamId: string;
}): Promise<boolean> => {
  const { data, error } = await supabase
    .from("talent_hub_candidate_interview_access_request")
    .select("*")
    .eq("requested_by_team_id", teamId)
    .eq("requested_by_recruiter_id", recruiterId)
    .eq("interview_flow_id", interviewFlowId)
    .eq("candidate_id", candidateId);

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return data.length > 0;
};

export const isExistingRequest = async ({
  interviewFlowId,
  candidateId,
}: {
  interviewFlowId: string;
  candidateId: string;
}): Promise<boolean> => {
  const { data, error } = await supabase
    .from("talent_hub_candidate_interview_access_request")
    .select("*")
    .eq("interview_flow_id", interviewFlowId)
    .eq("candidate_id", candidateId);

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return data.length > 0;
};

export const isCandidateInterviewPublished = async ({
  interviewFlowId,
  candidateId,
}: {
  interviewFlowId: string;
  candidateId: string;
}): Promise<boolean> => {
  const { data, error } = await supabase
    .from("candidate_published_interview_call")
    .select("is_published")
    .eq("interview_flow_id", interviewFlowId)
    .eq("candidate_id", candidateId);

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return data?.[0]?.is_published;
};

export const publishCandidateInterview = async ({
  interviewFlowId,
  candidateId,
  isPublished,
}: {
  interviewFlowId: string;
  candidateId: string;
  isPublished: boolean;
}): Promise<void> => {
  const { error } = await supabase
    .from("candidate_published_interview_call")
    .upsert({
      is_published: isPublished,
      interview_flow_id: interviewFlowId,
      candidate_id: candidateId,
    });

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }
};
